import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Button } from "react-migration/components/Button";
import { Icon } from "react-migration/components/Icon";
import { Notification } from "react-migration/components/Notification";
import { Project } from "@gql/sites-api/graphql";
import { ProjectHatch } from "../ProjectHatch";
import { ProjectInfo } from "./ProjectInfo";
import { SkeletonLoading } from "react-migration/components/SkeletonLoading";
import { TextInputV2 } from "react-migration/components/TextInputV2";
import { Theme } from "react-migration/lib/theme/Theme";
import { Tooltip } from "react-migration/components/Tooltip";
import clsx from "clsx";
import { useIsMobile } from "react-migration/lib/theme/hooks/useIsMobile";
import { useProjectSearch } from "./useProjectSearch";
import { useProjectsDropdownList } from "./useProjectsDropdownList";
import { ActionsMenu } from "../ActionsMenu";

const MAX_RECENT_PROJECTS = 5;
const ITEM_HEIGHT = 48;

let numberOfRecentProjects = MAX_RECENT_PROJECTS;

interface ProjectDropdownListProps {
  onSelectProject: (projectId: string) => void;
}

export const ProjectDropdownList = memo(({ onSelectProject }: ProjectDropdownListProps) => {
  const { selected, list, loading, error } = useProjectsDropdownList();
  const { searchQuery, filteredList, handleSearchChange } = useProjectSearch({ list, selected });
  const isSearching = searchQuery.trim().length > 0;
  const hasNoResults = isSearching && filteredList.length === 0;
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [focusedIndex, setFocusedIndex] = useState<number>(-1);
  const isMobile = useIsMobile();
  const searchable = isMobile ? true : list.length + (selected ? 1 : 0) > MAX_RECENT_PROJECTS;

  useEffect(
    function focusSearchInput() {
      if (searchable && searchInputRef.current) {
        searchInputRef.current?.focus();
      }
    },
    [loading, searchable]
  );

  useEffect(
    function resetFocusedIndex() {
      setFocusedIndex(-1);
    },
    [searchQuery]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      const displayList = filteredList.slice(0, MAX_RECENT_PROJECTS);
      const maxIndex = displayList.length - 1;

      if (e.key === "ArrowDown") {
        e.preventDefault();
        setFocusedIndex((prev) => (prev < maxIndex ? prev + 1 : 0));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setFocusedIndex((prev) => (prev > 0 ? prev - 1 : maxIndex));
      } else if (e.key === "Enter") {
        e.preventDefault();

        if (focusedIndex === -1 && displayList?.length) {
          onSelectProject(displayList[0].id);
        } else if (focusedIndex >= 0 && displayList[focusedIndex]) {
          onSelectProject(displayList[focusedIndex].id);
        }
      }
    },
    [filteredList, focusedIndex, onSelectProject]
  );

  // Prevent subsequent loads/renders from causing the dropdown to jump
  const loadingSkeletonDimensions = useMemo(
    function getLoadingSkeletonDimensions() {
      numberOfRecentProjects = Math.min(Math.floor(list.length + 1), MAX_RECENT_PROJECTS);

      return {
        height: numberOfRecentProjects * ITEM_HEIGHT + (numberOfRecentProjects - 1) * 8,
        rows: numberOfRecentProjects,
      };
    },
    [list.length]
  );

  if (error) {
    return (
      <Notification.Error
        title="An error occurred"
        message={`There was an issue loading your workspaces. Please try again later, or contact support if the problem persists.`}
      />
    );
  }

  return (
    <div
      className={clsx("flex flex-col gap-y-6 md:gap-y-3 select-none", {
        "h-full overflow-hidden": isMobile,
      })}
    >
      {searchable && (
        <TextInputV2
          ref={searchInputRef}
          placeholder="Search workspaces"
          size={isMobile ? "medium" : "x-small"}
          autoComplete="off"
          maxLength={50}
          value={searchQuery}
          onChange={handleSearchChange}
          onKeyDown={handleKeyDown}
          IconLeft={<Icon size="md" icon="icon-lt-search-line" extraClasses="text-neutral-400" />}
        />
      )}
      {loading ? (
        <div className="relative" style={{ height: loadingSkeletonDimensions.height }}>
          <SkeletonLoading rows={loadingSkeletonDimensions.rows} gap={2} showPadding={false} />
        </div>
      ) : (
        <div className={clsx("flex flex-col gap-y-2", { "overflow-y-auto h-full": isMobile })}>
          {hasNoResults ? (
            <EmptySearchState searchQuery={searchQuery} />
          ) : (
            filteredList.slice(0, isMobile ? filteredList.length : MAX_RECENT_PROJECTS).map((project, index) => {
              const isSelected = project.id === selected?.id;
              const isFocused = searchable && focusedIndex === index;

              return isSelected ? (
                <ProjectListItemSelected key={project.id} {...project} isFocused={isFocused} />
              ) : (
                <ProjectListItemButton key={project.id} {...project} onSelect={onSelectProject} isFocused={isFocused} />
              );
            })
          )}
        </div>
      )}
    </div>
  );
});

ProjectDropdownList.displayName = "ProjectDropdownList";

type ProjectListItemProps = Pick<
  Project,
  "id" | "title" | "developmentType" | "image" | "imageUrl" | "lastOpened" | "sitesCount"
>;

type ProjectListItemButtonProps = ProjectListItemProps & {
  onSelect: (projectId: string) => void;
  isFocused?: boolean;
};

const ProjectListItemButton = ({ onSelect, isFocused, ...projectProps }: ProjectListItemButtonProps) => {
  const { id } = projectProps;
  const buttonRef = useRef<HTMLButtonElement>(null);

  const handleSelect = useCallback(() => {
    onSelect(id);
  }, [onSelect, id]);

  return (
    <Button
      ref={buttonRef}
      variant="ghost"
      size="md"
      className={clsx("p-1! w-full justify-start", {
        "bg-background-button-tertiary!": isFocused,
      })}
      onClick={handleSelect}
      style={{ height: ITEM_HEIGHT }}
    >
      <ProjectListItemContent {...projectProps} />
    </Button>
  );
};

const ProjectListItemSelected = ({ isFocused, ...rest }: Omit<ProjectListItemButtonProps, "onSelect">) => (
  <div
    className={clsx("p-1 cursor-default rounded-md", {
      "bg-neutral-100": isFocused,
    })}
    style={{ height: ITEM_HEIGHT }}
  >
    <ProjectListItemContent {...rest} isSelected />
  </div>
);

const ProjectListItemContent = ({ isSelected, ...project }: ProjectListItemProps & { isSelected?: boolean }) => {
  const { title, developmentType, image, imageUrl, lastOpened, sitesCount } = project;

  return (
    <div className="flex items-center gap-x-2 w-full overflow-hidden">
      <ProjectHatch size="md" developmentType={developmentType} image={image} imageSrc={imageUrl ?? undefined} />
      <div className="flex flex-col items-start overflow-hidden">
        <div
          className={clsx("max-w-full", "text-sm font-medium", "flex flex-row gap-x-1", {
            "text-text-base-primary": isSelected,
            "text-text-base-active": !isSelected,
          })}
        >
          <Tooltip.Wrapper>
            <Tooltip.Trigger asChild>
              <span className="truncate">{title}</span>
            </Tooltip.Trigger>
            <Tooltip.Content align="start" side="top" theme={Theme.Light} size="sm">
              <span className="whitespace-normal wrap-break-word">{title}</span>
            </Tooltip.Content>
          </Tooltip.Wrapper>
          {isSelected && <span className="text-text-base-primary-disabled font-normal">(selected)</span>}
        </div>
        <ProjectInfo date={lastOpened} sitesCount={sitesCount} />
      </div>

      <div className="ml-auto">
        <ActionsMenu project={project} />
      </div>
    </div>
  );
};

interface EmptySearchStateProps {
  searchQuery: string;
}

const EmptySearchState = ({ searchQuery }: EmptySearchStateProps) => (
  <div className="flex flex-col items-center justify-center gap-y-2">
    <div className="rounded-full w-12 h-12 flex items-center justify-center bg-background-surface-neutral-subtle">
      <Icon size="md" icon="icon-lt-seo-line" extraClasses="text-text-base-secondary" />
    </div>
    <div className="px-4 text-center text-sm font-normal text-text-base-secondary">
      No workspaces found matching &ldquo;{searchQuery}&rdquo;
    </div>
  </div>
);
